import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { rows } from "@/lib/api";
import useInfiniteList from "@/hooks/useInfiniteList";
import ProductCard from "@/components/store/ProductCard";
import { Select } from "@/components/ui-kit";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);

  const category = params.get("categoria") || "";
  const search = params.get("busca") || "";
  const sort = params.get("ordem") || "";

  useEffect(() => { api.get("/categories").then((r) => setCategories(rows(r))); }, []);

  const {
    items: products, total, sentinelRef,
    isLoading: loading, isFetchingNextPage, hasNextPage,
  } = useInfiniteList("/products", {
    category: category || undefined,
    search: search || undefined,
    sort: sort || undefined,
  });

  const setParam = (key, value) => {
    const p = new URLSearchParams(params);
    if (value) p.set(key, value); else p.delete(key);
    setParams(p);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
      <aside className="lg:col-span-3">
        <h3 className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-3">Categorias</h3>
        <div className="space-y-1">
          <button onClick={() => setParam("categoria", "")} data-testid="filter-category-all"
            className={`block w-full text-left px-3 py-2 rounded-lg text-sm ${!category ? "bg-brand-50 text-brand-700 font-semibold" : "text-slate-700 hover:bg-slate-100"}`}>Todas</button>
          {categories.map((c) => (
            <button key={c.slug} onClick={() => setParam("categoria", c.slug)} data-testid={`filter-category-${c.slug}`}
              className={`block w-full text-left px-3 py-2 rounded-lg text-sm ${category === c.slug ? "bg-brand-50 text-brand-700 font-semibold" : "text-slate-700 hover:bg-slate-100"}`}>{c.name}</button>
          ))}
        </div>
        <h3 className="text-xs font-semibold tracking-wider uppercase text-slate-500 mt-6 mb-3">Ordenar</h3>
        <Select value={sort} onChange={(e) => setParam("ordem", e.target.value)} data-testid="sort-select">
          <option value="">Relevância</option>
          <option value="price_asc">Menor preço</option>
          <option value="price_desc">Maior preço</option>
          <option value="name">Nome (A-Z)</option>
        </Select>
      </aside>
      <div className="lg:col-span-9">
        <div className="flex items-baseline justify-between mb-6">
          <h1 className="font-heading text-2xl font-semibold text-slate-900">
            {search ? `Resultados para "${search}"` : categories.find((c) => c.slug === category)?.name || "Todos os produtos"}
          </h1>
          <span className="text-sm text-slate-500" data-testid="products-count">{total} produto(s)</span>
        </div>
        {loading ? (
          <p className="text-slate-500">Carregando...</p>
        ) : products.length === 0 ? (
          <p className="text-slate-500" data-testid="no-products-msg">Nenhum produto encontrado.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5" data-testid="products-grid">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
            {/* Sentinela do scroll infinito: ao entrar na viewport, puxa a próxima página. */}
            <div ref={sentinelRef} aria-hidden className="h-px" />
            {isFetchingNextPage && (
              <p className="text-sm text-slate-500 text-center py-6" data-testid="products-loading-more">
                Carregando mais produtos...
              </p>
            )}
            {!hasNextPage && products.length > 0 && (
              <p className="text-sm text-slate-400 text-center py-6" data-testid="products-end">
                Você viu todos os {total} produtos.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
